import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios.js';
import useAuth from 'app/hooks/useAuth';
import useReferenceServerInfo from 'app/hooks/useReferenceSystemServerInfo';
import KlientInfoDialog from 'app/components/KlientInfoDialog/KlientInfoDialog';
import CurrentClientOrderCard from 'app/components/Telephony/CurrentClientOrderCard';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Chip, Box, Avatar } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import history from 'history.js';
import { getClientInfo, searchClients } from 'app/services/clientDirectoryApi';
import { ORDER_PERMISSIONS, hasOrderPermission } from 'app/constants/orderPermissions';

const TelephonyContext = createContext({
    calls: [],
    queueCalls: [],
    activeCall: null,
    telephonyMeta: {
        integrationEnabled: false,
        canEditIntegration: false,
        operatorEnabled: false,
        operatorInternalLine: '',
        operatorEmployeeExternalId: '',
        callPopupMode: 'incoming',
        orderOpenMode: 'editor',
        operatorPaused: false,
        operatorBusy: false,
        isOnline: false,
        lastPollAt: 0,
        lastErrorAt: 0,
    },
    triggerFakeCall: () => {},
    requestFakeCall: () => {},
    openCallDialog: () => {},
    minimizeCallDialog: () => {},
    closeActiveCall: () => {},
    markCallAsAccepted: () => {},
    markCallAsCallbackDone: () => {},
    setOperatorPause: () => {},
    openCallById: () => {},
});

const useStyles = makeStyles((theme) => ({
    dialogTitle: {
        paddingBottom: theme.spacing(1),
    },
    titleRow: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1),
    },
    callBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: theme.spacing(0.75),
        fontSize: 12,
        color: theme.palette.text.secondary,
    },
    pulseDot: {
        width: 8,
        height: 8,
        borderRadius: '50%',
        background: theme.palette.error.main,
        boxShadow: `0 0 0 0 ${theme.palette.error.main}88`,
        animation: '$pulse 1.5s infinite',
    },
    '@keyframes pulse': {
        '0%': { boxShadow: `0 0 0 0 ${theme.palette.error.main}88` },
        '70%': { boxShadow: `0 0 0 10px ${theme.palette.error.main}00` },
        '100%': { boxShadow: `0 0 0 0 ${theme.palette.error.main}00` },
    },
    numberRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.25),
        marginBottom: theme.spacing(1),
    },
    phoneAvatar: {
        width: 42,
        height: 42,
        fontSize: 18,
        fontWeight: 700,
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
    },
    metaRow: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'wrap',
        marginBottom: theme.spacing(1.5),
    },
    infoCard: {
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 10,
        padding: theme.spacing(1.25),
        background: theme.palette.background.default,
    },
    infoLabel: {
        fontSize: 12,
        color: theme.palette.text.secondary,
        marginBottom: theme.spacing(0.5),
    },
    infoValue: {
        fontSize: 14,
        fontWeight: 500,
    },
    infoSub: {
        fontSize: 13,
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    actionsRow: {
        display: 'flex',
        gap: theme.spacing(1),
        alignItems: 'center',
        '& .MuiButton-root': {
            flexShrink: 0,
            whiteSpace: 'nowrap',
        },
        [theme.breakpoints.down('sm')]: {
            '&:first-child': {
                justifyContent: 'flex-start',
            },
            '&:last-child': {
                justifyContent: 'flex-end',
            },
        },
    },
    lightDialogPaper: {
        backgroundColor: '#ffffff',
        color: '#111827',
        borderRadius: 12,
        border: '1px solid #e5e7eb',
        boxShadow: '0 18px 36px rgba(15, 23, 42, 0.18)',
    },
    dialogHeader: {
        background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 100%)',
        borderBottom: '1px solid #e5e7eb',
        paddingTop: 14,
        paddingBottom: 10,
    },
    dialogBody: {
        backgroundColor: '#ffffff',
        color: '#111827',
    },
    dialogFooter: {
        borderTop: '1px solid #e5e7eb',
        backgroundColor: '#ffffff',
        padding: '10px 16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: theme.spacing(1),
        flexWrap: 'nowrap',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'stretch',
        },
    },
    btnPrimary: {
        backgroundColor: '#2563eb',
        color: '#ffffff',
        textTransform: 'none',
        fontWeight: 600,
        '&:hover': {
            backgroundColor: '#1d4ed8',
        },
    },
    btnGhost: {
        border: '1px solid #cbd5e1',
        color: '#0f172a',
        textTransform: 'none',
        fontWeight: 600,
        backgroundColor: '#ffffff',
        '&:hover': {
            backgroundColor: '#f8fafc',
            borderColor: '#94a3b8',
        },
    },
}));

const normalizePhone = (phone) => {
    if (!phone) return '';
    return String(phone).replace(/[^\d]/g, '');
};

const formatPhoneHuman = (phone) => {
    const digits = normalizePhone(phone);
    if (digits.length === 11 && (digits.startsWith('7') || digits.startsWith('8'))) {
        const p = digits.replace(/^8/, '7');
        return `+7 (${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}`;
    }
    if (digits.length === 10) {
        return `+7 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 8)}-${digits.slice(8, 10)}`;
    }
    return phone;
};

const RINGING_STALE_TTL_MS = 5 * 60 * 1000;

const isRingingStatus = (status) => {
    const st = String(status || '').toLowerCase();
    return st === 'ringing' || st === 'incoming';
};

const isOperatorActiveStatus = (status) => {
    const st = String(status || '').toLowerCase();
    return st === 'answered' || st === 'outgoing';
};

const isMissedStatus = (status) => {
    const st = String(status || '').toLowerCase();
    return st === 'missed';
};

const isStaleRingingCall = (call, nowTs = Date.now()) => {
    if (!call || !isRingingStatus(call.status)) return false;
    const ts = Number(call.timestamp || 0);
    if (!ts) return false;
    return (nowTs - ts) > RINGING_STALE_TTL_MS;
};

const getCallTimeLabel = (status) => {
    const st = String(status || '').toLowerCase();
    if (st === 'ringing' || st === 'incoming') return 'Поступил';
    if (st === 'answered' || st === 'outgoing') return 'Принят';
    if (st === 'ended' || st === 'hangup') return 'Завершен';
    if (st === 'missed') return 'Пропущен';
    return 'Событие';
};

const normalizeInternalComparable = (value) => {
    const cleaned = String(value || '').trim().replace(/\s+/g, '');
    if (!cleaned) return '';
    if (/^\d+$/.test(cleaned)) {
        const noZeros = cleaned.replace(/^0+/, '');
        return noZeros || '0';
    }
    return cleaned;
};

const getOperatorUiSettingsStorageKey = (user) => {
    const userId = String(user?.id || user?.user_id || user?.email || 'guest').trim();
    return `pixelcrm_telephony_operator_ui_${userId}`;
};

const readOperatorUiSettings = (user) => {
    try {
        if (typeof window === 'undefined' || !window.localStorage) return {};
        const raw = window.localStorage.getItem(getOperatorUiSettingsStorageKey(user));
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === 'object' ? parsed : {};
    } catch (e) {
        return {};
    }
};

const getCallPopupModeFromSettings = (user, data = {}, operator = {}) => {
    const rawMode = operator?.call_popup_mode ?? data?.call_popup_mode;
    const modeFromApi = String(rawMode || '').trim();
    if (modeFromApi === 'answered' || modeFromApi === 'incoming') return modeFromApi;
    const modeFromStorage = String(readOperatorUiSettings(user)?.call_popup_mode || '').trim();
    if (modeFromStorage === 'answered' || modeFromStorage === 'incoming') return modeFromStorage;
    return 'incoming';
};

const getOrderOpenModeFromSettings = (user, data = {}, operator = {}) => {
    const rawMode = operator?.order_open_mode ?? data?.order_open_mode;
    const modeFromApi = String(rawMode || '').trim();
    if (modeFromApi === 'list' || modeFromApi === 'editor') return modeFromApi;
    const modeFromStorage = String(readOperatorUiSettings(user)?.order_open_mode || '').trim();
    if (modeFromStorage === 'list' || modeFromStorage === 'editor') return modeFromStorage;
    return 'editor';
};

const getOrderDayRangeForUrl = (order = {}) => {
    const timestamp = Number(order?.date_delivered || order?.date_create || 0);
    const date = timestamp > 0 ? new Date(timestamp * 1000) : new Date();
    const pad = (value) => String(value).padStart(2, '0');
    const format = (value) => (
        `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`
        + `T${pad(value.getHours())}:${pad(value.getMinutes())}`
    );
    const start = new Date(date);
    start.setHours(0, 0, 0, 0);
    const stop = new Date(date);
    stop.setHours(23, 59, 0, 0);
    return { start: format(start), stop: format(stop) };
};

export const TelephonyProvider = ({ children }) => {
    const classes = useStyles();
    const { user } = useAuth();
    const canEditOrders = hasOrderPermission(user, ORDER_PERMISSIONS.EDIT);
    const canViewOrders = hasOrderPermission(user, 'ALLORDER');
    const { referenceStatusOrderAll } = useReferenceServerInfo();

    const [calls, setCalls] = useState([]);
    const [queueCalls, setQueueCalls] = useState([]);
    const [activeCall, setActiveCall] = useState(null);
    const [telephonyMeta, setTelephonyMeta] = useState({
        integrationEnabled: false,
        canEditIntegration: false,
        operatorEnabled: false,
        operatorInternalLine: '',
        operatorEmployeeExternalId: '',
        callPopupMode: 'incoming',
        orderOpenMode: 'editor',
        operatorPaused: false,
        operatorBusy: false,
        isOnline: false,
        lastPollAt: 0,
        lastErrorAt: 0,
    });
    const [klientInfo, setKlientInfo] = useState(null);
    const [klientCardOpen, setKlientCardOpen] = useState(false);
    const [callDialogMinimized, setCallDialogMinimized] = useState(false);
    const suppressedCallIdsRef = useRef(new Map());
    const [canPoll, setCanPoll] = useState(false);
    const orderOpenMode = telephonyMeta?.orderOpenMode || 'editor';

    const loadTelephonyState = async () => {
        try {
            const res = await axios.get('/api/telephony/settings');
            const data = res?.data?.data || {};
            const integration = data?.integration || {};
            const operator = data?.operator || {};
            const integrationEnabled = !!(integration?.enabled ?? data?.enabled);
            const canEditIntegration = !!(data?.can_edit_integration);
            const operatorEnabled = !!(operator?.receive_calls ?? data?.receive_calls ?? true);
            const operatorInternalLine = String(operator?.internal_line ?? data?.internal_line ?? '').trim();
            const operatorEmployeeExternalId = String(operator?.employee_external_id ?? data?.employee_external_id ?? '').trim();
            const callPopupMode = getCallPopupModeFromSettings(user, data, operator);
            const orderOpenMode = getOrderOpenModeFromSettings(user, data, operator);
            const hasInternalLine = operatorInternalLine.length > 0;
            const allowPoll = integrationEnabled && operatorEnabled && hasInternalLine;
            setCanPoll(allowPoll);
            setTelephonyMeta((prev) => ({
                ...prev,
                integrationEnabled,
                canEditIntegration,
                operatorEnabled,
                operatorInternalLine,
                operatorEmployeeExternalId,
                callPopupMode,
                orderOpenMode,
                operatorPaused: !operatorEnabled,
                isOnline: allowPoll ? prev.isOnline : false,
            }));
            if (!allowPoll) {
                setCalls([]);
                setQueueCalls([]);
                setActiveCall(null);
            }
        } catch (e) {
            setCanPoll(false);
            setCalls([]);
            setQueueCalls([]);
            setActiveCall(null);
            setTelephonyMeta((prev) => ({
                ...prev,
                integrationEnabled: false,
                canEditIntegration: false,
                operatorEnabled: false,
                operatorInternalLine: '',
                operatorEmployeeExternalId: '',
                callPopupMode: 'incoming',
                orderOpenMode: 'editor',
                operatorPaused: true,
                isOnline: false,
                lastErrorAt: Date.now(),
            }));
        }
    };

    useEffect(() => {
        loadTelephonyState();
        const interval = setInterval(loadTelephonyState, 30000);
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        const handleUiSettingsChanged = (event) => {
            const nextMode = String(event?.detail?.orderOpenMode || '').trim();
            if (nextMode !== 'list' && nextMode !== 'editor') return;
            setTelephonyMeta((prev) => ({ ...prev, orderOpenMode: nextMode }));
        };
        window.addEventListener('telephony-ui-settings-changed', handleUiSettingsChanged);
        return () => window.removeEventListener('telephony-ui-settings-changed', handleUiSettingsChanged);
    }, []);

    const activeCallClientId = activeCall?.klientId;
    const activeCallFrom = activeCall?.from;

    // Подтягиваем карточку клиента по id, если он пришёл вместе со звонком
    useEffect(() => {
        let mounted = true;
        const loadKlient = async () => {
            let clientId = Number(activeCallClientId || 0);
            if (!clientId && activeCallFrom) {
                const norm = normalizePhone(activeCallFrom);
                try {
                    const matches = await searchClients(norm, 5);
                    const match = matches.find((item) => normalizePhone(item.phone) === norm);
                    clientId = Number(match?.id || 0);
                } catch (e) {
                    console.error('Ошибка поиска клиента по номеру', e);
                }
            }

            if (!clientId) {
                setKlientInfo(null);
                return;
            }

            try {
                const data = await getClientInfo(clientId);
                if (mounted) setKlientInfo(data);
            } catch (e) {
                console.error('Ошибка загрузки клиента по звонку', e);
            }
        };
        loadKlient();
        return () => { mounted = false; };
    }, [activeCallClientId, activeCallFrom]);

    // Поллинг звонков
    useEffect(() => {
        if (!canPoll) return undefined;
        let interval;
        const fetchCalls = async () => {
            try {
                const res = await axios.get('/api/telephony/poll');
                const sourceList = Array.isArray(res.data?.calls) ? res.data.calls : (Array.isArray(res.data) ? res.data : []);
                const nowTs = Date.now();
                const list = sourceList.filter((c) => !isStaleRingingCall(c, nowTs));
                setCalls(list);
                const liveIds = new Set(list.map((c) => c?.callId).filter(Boolean));
                suppressedCallIdsRef.current.forEach((suppressedAt, callId) => {
                    const expired = (nowTs - Number(suppressedAt || 0)) > 5 * 60 * 1000;
                    if (expired || !liveIds.has(callId)) {
                        suppressedCallIdsRef.current.delete(callId);
                    }
                });
                setTelephonyMeta((prev) => ({
                    ...prev,
                    isOnline: true,
                    lastPollAt: Date.now(),
                }));
                const ringing = list.find((c) => {
                    if (!c || !isRingingStatus(c.status)) return false;
                    const callId = c.callId;
                    if (!callId) return true;
                    const suppressedAt = suppressedCallIdsRef.current.get(callId);
                    if (!suppressedAt) return true;
                    const eventTs = Number(c.timestamp || 0);
                    if (eventTs > Number(suppressedAt)) {
                        suppressedCallIdsRef.current.delete(callId);
                        return true;
                    }
                    return false;
                });

                const activeCallId = activeCall?.callId || '';
                const activeFromList = activeCallId ? list.find((c) => (c?.callId || '') === activeCallId) : null;
                const operatorLine = String(telephonyMeta?.operatorInternalLine || '').trim();
                const operatorEmployeeId = String(telephonyMeta?.operatorEmployeeExternalId || '').trim();
                const operatorLineNorm = normalizeInternalComparable(operatorLine);
                const operatorEmployeeNorm = normalizeInternalComparable(operatorEmployeeId);
                const popupMode = String(telephonyMeta?.callPopupMode || 'incoming').trim();
                const getCallInternal = (call) => String(
                    call?.internal
                    || call?.payload?.internal
                    || call?.payload?.last_internal
                    || ''
                ).trim();
                const getCallEmployeeId = (call) => String(
                    call?.payload?.employee_info?.employee_id
                    || call?.payload?.employee_id
                    || ''
                ).trim();
                const isCallForCurrentOperator = (call) => {
                    const callInternal = getCallInternal(call);
                    const callEmployeeId = getCallEmployeeId(call);
                    const callInternalNorm = normalizeInternalComparable(callInternal);
                    const callEmployeeNorm = normalizeInternalComparable(callEmployeeId);
                    if (operatorEmployeeNorm !== '' && callEmployeeNorm !== '') return operatorEmployeeNorm === callEmployeeNorm;
                    if (operatorLineNorm !== '' && callInternalNorm !== '') return operatorLineNorm === callInternalNorm;
                    if (operatorLineNorm !== '' && callEmployeeNorm !== '') return operatorLineNorm === callEmployeeNorm;
                    return false;
                };
                const queue = list.filter((c) => {
                    if (!c) return false;
                    if (isRingingStatus(c.status)) return true;
                    if (isOperatorActiveStatus(c.status) && isCallForCurrentOperator(c)) return true;
                    if (isMissedStatus(c.status)) return true;
                    return false;
                });
                setQueueCalls(queue);
                const answeredCurrent = list.find((c) => {
                    if (!c) return false;
                    if (!isOperatorActiveStatus(c.status)) return false;
                    return isCallForCurrentOperator(c);
                });
                if (activeFromList) {
                    const st = String(activeFromList?.status || '').toLowerCase();
                    const staleRinging = isStaleRingingCall(activeFromList, nowTs);
                    const callInternal = getCallInternal(activeFromList);
                    const callEmployeeId = getCallEmployeeId(activeFromList);
                    const callInternalNorm = normalizeInternalComparable(callInternal);
                    const callEmployeeNorm = normalizeInternalComparable(callEmployeeId);
                    const answeredByOtherByLine = operatorLineNorm !== '' && callInternalNorm !== '' && operatorLineNorm !== callInternalNorm && (st === 'answered' || st === 'outgoing');
                    const answeredByOtherByEmployee = operatorEmployeeNorm !== '' && callEmployeeNorm !== '' && operatorEmployeeNorm !== callEmployeeNorm && (st === 'answered' || st === 'outgoing');
                    const answeredByOther = answeredByOtherByLine || answeredByOtherByEmployee;
                    const endedState = st === 'ended' || st === 'hangup' || st === 'record_ready';

                    if (answeredByOther || endedState || staleRinging) {
                        if (activeFromList?.callId) {
                            suppressedCallIdsRef.current.set(activeFromList.callId, Date.now());
                        }
                        setActiveCall(null);
                        setKlientInfo(null);
                        setKlientCardOpen(false);
                        setCallDialogMinimized(false);
                    } else {
                        setActiveCall(activeFromList);
                        setTelephonyMeta((prev) => ({ ...prev, operatorBusy: isOperatorActiveStatus(st) }));
                    }
                } else {
                    if (answeredCurrent) {
                        setActiveCall(answeredCurrent);
                        setTelephonyMeta((prev) => ({ ...prev, operatorBusy: true }));
                        if (popupMode === 'answered') {
                            setCallDialogMinimized(false);
                        }
                    } else {
                        setTelephonyMeta((prev) => ({ ...prev, operatorBusy: false }));
                    }
                }

                const activeState = String(activeFromList?.status || activeCall?.status || '').toLowerCase();
                const operatorBusyNow = isOperatorActiveStatus(activeState) || !!answeredCurrent;
                if (!operatorBusyNow && (!activeCall || ringing?.callId !== activeCall.callId)) {
                    if (popupMode === 'incoming') {
                        if (ringing) {
                            setActiveCall(ringing);
                            setCallDialogMinimized(false);
                        }
                    }
                }
            } catch (e) {
                // тихо глотаем, чтобы не шуметь пользователю
                setTelephonyMeta((prev) => ({
                    ...prev,
                    isOnline: false,
                    lastErrorAt: Date.now(),
                }));
                console.error('telephony poll error', e);
            }
        };
        fetchCalls();
        interval = setInterval(fetchCalls, 5000);
        return () => clearInterval(interval);
    }, [canPoll, activeCall?.callId, telephonyMeta?.operatorInternalLine, telephonyMeta?.operatorEmployeeExternalId]);

    const closeActive = () => {
        if (activeCall?.callId) {
            suppressedCallIdsRef.current.set(activeCall.callId, Date.now());
        }
        setActiveCall(null);
        setKlientInfo(null);
        setKlientCardOpen(false);
        setCallDialogMinimized(false);
        setTelephonyMeta((prev) => ({ ...prev, operatorBusy: false }));
    };

    const markCallAsAccepted = (callId) => {
        const id = String(callId || '').trim();
        if (!id) return;
        suppressedCallIdsRef.current.set(id, Date.now());
        setQueueCalls((prev) => (Array.isArray(prev) ? prev.filter((c) => String(c?.callId || '') !== id) : []));
        setCalls((prev) => (Array.isArray(prev) ? prev.filter((c) => String(c?.callId || '') !== id) : []));
        if (String(activeCall?.callId || '') === id) {
            setActiveCall(null);
            setKlientInfo(null);
            setKlientCardOpen(false);
            setCallDialogMinimized(false);
        }
    };

    const markCallAsCallbackDone = async (call) => {
        const callId = String(call?.rawCallId || call?.callId || '').trim();
        const pbxCallId = String(call?.pbxCallId || call?.callId || '').trim();
        const phoneFrom = String(call?.from || '').trim();
        const phoneTo = String(call?.to || '').trim();
        const timestamp = Number(call?.timestamp || 0);
        if (!callId && !pbxCallId && (!phoneFrom || !timestamp)) return false;
        try {
            const res = await axios.post('/api/telephony/call/callback-done', {
                call_id: callId || undefined,
                pbx_call_id: pbxCallId || undefined,
                phone_from: phoneFrom || undefined,
                phone_to: phoneTo || undefined,
                timestamp: timestamp || undefined,
            });
            const updated = Number(res?.data?.data?.updated || 0);
            if (updated <= 0) {
                console.warn('telephony callback done: updated=0', res?.data);
                return false;
            }
            if (callId) {
                suppressedCallIdsRef.current.set(callId, Date.now());
            }
            setQueueCalls((prev) => (Array.isArray(prev)
                ? prev.filter((c) =>
                    String(c?.callId || '') !== callId
                    && String(c?.rawCallId || '') !== callId
                    && String(c?.pbxCallId || '') !== pbxCallId
                )
                : []));
            setCalls((prev) => (Array.isArray(prev)
                ? prev.filter((c) =>
                    String(c?.callId || '') !== callId
                    && String(c?.rawCallId || '') !== callId
                    && String(c?.pbxCallId || '') !== pbxCallId
                )
                : []));
            if (
                String(activeCall?.callId || '') === callId
                || String(activeCall?.pbxCallId || '') === pbxCallId
            ) {
                setActiveCall(null);
                setKlientInfo(null);
                setKlientCardOpen(false);
                setCallDialogMinimized(false);
            }
            return true;
        } catch (e) {
            console.error('telephony callback done error', e);
            return false;
        }
    };

    const openCallDialog = () => {
        if (activeCall) setCallDialogMinimized(false);
    };

    const minimizeCallDialog = () => {
        if (activeCall) setCallDialogMinimized(true);
    };

    const setOperatorPause = async (pause) => {
        const shouldPause = !!pause;
        const receiveCalls = shouldPause ? 0 : 1;
        const line = String(telephonyMeta?.operatorInternalLine || '').trim();
        if (!line) return false;
        try {
            await axios.post('/api/telephony/settings/save', {
                receive_calls: receiveCalls,
                internal_line: line,
                employee_external_id: String(telephonyMeta?.operatorEmployeeExternalId || '').trim(),
            });
            await loadTelephonyState();
            return true;
        } catch (e) {
            console.error('telephony pause update error', e);
            return false;
        }
    };

    const openCallById = (callId) => {
        const id = String(callId || '').trim();
        if (!id) return;
        const found = (Array.isArray(calls) ? calls : []).find((c) => String(c?.callId || '') === id);
        if (found) {
            if (found.callId) {
                suppressedCallIdsRef.current.delete(found.callId);
            }
            setActiveCall(found);
            setCallDialogMinimized(false);
        }
    };

    const triggerFakeCall = (payload = {}) => {
        const fake = {
            callId: `fake_${Date.now()}`,
            from: '+79990000000',
            to: user?.email || 'operator',
            status: 'ringing',
            timestamp: Date.now(),
            ...payload,
        };
        if (fake.callId) {
            suppressedCallIdsRef.current.delete(fake.callId);
        }
        setCalls((prev) => [fake, ...prev]);
        setActiveCall(fake);
        setCallDialogMinimized(false);
    };

    const requestFakeCall = async (payload = {}) => {
        try {
            const res = await axios.post('/api/telephony/fake-call', payload);
            const call = res.data?.call || res.data || null;
            if (call) {
                if (call.callId) {
                    suppressedCallIdsRef.current.delete(call.callId);
                }
                setCalls((prev) => [call, ...prev]);
                setActiveCall(call);
                setCallDialogMinimized(false);
                return call;
            }
        } catch (e) {
            console.warn('telephony fake-call fallback', e);
        }
        return triggerFakeCall(payload);
    };

    const handleOpenClientCard = () => {
        setKlientCardOpen(false);
        // Переходим в создание заказа и передаём телефон с маршрутом входящего номера.
        if (activeCall?.from) {
            const phone = normalizePhone(activeCall.from);
            const params = new URLSearchParams();
            params.set('phone', phone);
            if (Number(activeCall?.klientId || 0) > 0) {
                params.set('clientId', String(Number(activeCall.klientId)));
            }
            if (Number(activeCall?.affiliateId || 0) > 0) {
                params.set('affiliateId', String(Number(activeCall.affiliateId)));
            }
            if (Number(activeCall?.pointId || 0) > 0) {
                params.set('pointId', String(Number(activeCall.pointId)));
            }
            if (Number(activeCall?.brandId || 0) > 0) {
                params.set('brandId', String(Number(activeCall.brandId)));
            }
            if (String(activeCall?.city || activeCall?.affiliateName || '').trim()) {
                params.set('city', String(activeCall.city || activeCall.affiliateName).trim());
            }
            history.push(`/order/newOrderFastFood?${params.toString()}`);
        }
        // Карточку звонка сворачиваем, но не закрываем (нужна активная сессия звонка).
        setCallDialogMinimized(true);
    };

    const dialogContent = useMemo(() => {
        if (!activeCall) return null;
        const phoneRaw = activeCall.from || '';
        const phone = formatPhoneHuman(phoneRaw) || phoneRaw || '';
        const when = activeCall.timestamp ? new Date(activeCall.timestamp) : null;
        const rawInfo = klientInfo || {};
        const klientContainer = rawInfo.data || rawInfo.result || rawInfo;
        const klientData = klientContainer?.klient_information || klientContainer?.klient || null;
        const hasKlientCard = !!(klientData && typeof klientData === 'object' && Object.keys(klientData).length > 0);
        const label = activeCall.klientName || activeCall.clientName || klientData?.firstName || '';
        const statusRaw = String(activeCall?.status || '').toLowerCase();
        const isRingingNow = isRingingStatus(statusRaw);
        const isActiveTalk = isOperatorActiveStatus(statusRaw);
        const closeActionLabel = isActiveTalk ? 'Завершен' : 'Игнорировать';
        const timeLabel = getCallTimeLabel(statusRaw);
        const statusLabel = (() => {
            const st = statusRaw;
            switch (st) {
                case 'ringing':
                case 'incoming':
                    return 'Клиент звонит';
                case 'answered':
                    return 'Активный разговор';
                case 'outgoing':
                    return 'Активный разговор';
                case 'hangup':
                case 'ended':
                    return 'Завершён';
                case 'missed':
                    return 'Пропущен';
                default:
                    return st;
            }
        })();
        const statusColor = (() => {
            const st = statusRaw;
            if (st === 'ringing' || st === 'incoming') return 'primary';
            if (st === 'answered') return 'secondary';
            if (st === 'hangup') return 'default';
            return 'default';
        })();
        const lineLabel = activeCall?.to && activeCall.to !== 'operator' ? activeCall.to : null;
        const cityLabel = String(activeCall?.city || activeCall?.affiliateName || '').trim();
        const pointLabel = String(activeCall?.pointName || '').trim();
        const brandLabel = String(activeCall?.brandName || '').trim();
        const routeLabel = cityLabel
            ? `Город: ${cityLabel}${pointLabel ? ` · ${pointLabel}` : ''}${brandLabel ? ` · ${brandLabel}` : ''}`
            : 'Город не определён';
        const initials = (label || phone || '?').toString().trim().charAt(0).toUpperCase();
        return (
            <>
                <Dialog
                    open={!!activeCall && !callDialogMinimized}
                    onClose={() => setCallDialogMinimized(true)}
                    maxWidth="md"
                    fullWidth
                    PaperProps={{ className: classes.lightDialogPaper }}
                >
                    <DialogTitle disableTypography className={`${classes.dialogTitle} ${classes.dialogHeader}`}>
                        <div className={classes.titleRow}>
                            <div>
                                <Typography variant="h6" style={{ margin: 0 }}>Входящий звонок</Typography>
                                <div className={classes.callBadge}>
                                    <span className={classes.pulseDot} />
                                    <span>Линия активна</span>
                                </div>
                            </div>
                            {statusLabel ? <Chip size="small" color={statusColor} label={statusLabel} /> : null}
                        </div>
                    </DialogTitle>
                    <DialogContent dividers className={classes.dialogBody}>
                        <div>
                            <div className={classes.numberRow}>
                                <Avatar className={classes.phoneAvatar}>{initials}</Avatar>
                                <Box>
                                    <Typography variant="h5">{phone || 'Неизвестный номер'}</Typography>
                                    {label ? <Typography color="textSecondary" style={{ fontSize: 13 }}>{label}</Typography> : null}
                                </Box>
                            </div>
                            <div className={classes.metaRow}>
                                <Chip
                                    size="small"
                                    variant="outlined"
                                    label={routeLabel}
                                    title={cityLabel ? 'Определено по виртуальному номеру Novofon' : 'Для входящего номера не настроена привязка к филиалу и точке'}
                                />
                                {lineLabel ? <Chip size="small" variant="outlined" label={`Линия: ${lineLabel}`} /> : null}
                                {when ? <Chip size="small" variant="outlined" label={`${timeLabel}: ${when.toLocaleTimeString()}`} /> : null}
                            </div>
                            {klientData ? (
                                <div className={classes.infoCard}>
                                    <Typography className={classes.infoLabel}>Клиент найден</Typography>
                                    <Typography className={classes.infoValue}>
                                        {[klientData.firstName, klientData.lastName].filter(Boolean).join(' ') || 'Без имени'}
                                    </Typography>
                                    {klientData.email ? <Typography className={classes.infoSub}>{klientData.email}</Typography> : null}
                                    {klientData.comment ? <Typography className={classes.infoSub}>Заметка: {klientData.comment}</Typography> : null}
                                </div>
                            ) : (
                                <div className={classes.infoCard}>
                                    <Typography className={classes.infoLabel}>Клиент не найден в базе</Typography>
                                    <Typography className={classes.infoSub}>Можно сразу оформить новый заказ по номеру.</Typography>
                                </div>
                            )}
                            {hasKlientCard ? (
                                <CurrentClientOrderCard
                                    clientInfo={klientContainer}
                                    statusReferences={referenceStatusOrderAll}
                                    canOpenOrder={(orderOpenMode === 'editor' && canEditOrders) || canViewOrders}
                                    actionLabel={orderOpenMode === 'editor' && canEditOrders ? 'Редактировать заказ' : 'Показать заказ'}
                                    unavailableReason="Нет доступа к просмотру заказов"
                                    onOpenOrder={(orderId, order) => {
                                        if (orderOpenMode === 'editor' && canEditOrders) {
                                            history.push(`/order/editOrderFastFood/order_${orderId}`);
                                        } else {
                                            const params = new URLSearchParams();
                                            params.set('orderId', String(orderId));
                                            const clientId = Number(klientData?.id || klientContainer?.klient_information?.id || 0);
                                            const clientPhone = String(klientData?.phone || phone || '').trim();
                                            if (clientId > 0) params.set('clientId', String(clientId));
                                            if (clientPhone) params.set('phone', clientPhone);
                                            const range = getOrderDayRangeForUrl(order);
                                            params.set('from', range.start);
                                            params.set('to', range.stop);
                                            history.push(`/order/allOrderFastFood?${params.toString()}`);
                                        }
                                        setCallDialogMinimized(true);
                                    }}
                                />
                            ) : null}
                        </div>
                    </DialogContent>
                    <DialogActions className={classes.dialogFooter}>
                        <div className={classes.actionsRow}>
                            {isRingingNow ? (
                                <Button
                                    size="small"
                                    onClick={() => markCallAsCallbackDone(activeCall)}
                                    className={classes.btnGhost}
                                    variant="outlined"
                                >
                                    Уже принят
                                </Button>
                            ) : null}
                            <Button
                                size="small"
                                onClick={() => setCallDialogMinimized(true)}
                                className={classes.btnGhost}
                                variant="outlined"
                            >
                                Свернуть
                            </Button>
                            <Button
                                size="small"
                                onClick={closeActive}
                                variant="contained"
                                style={{ backgroundColor: '#dc2626', color: '#ffffff', textTransform: 'none', fontWeight: 600 }}
                            >
                                {closeActionLabel}
                            </Button>
                        </div>
                        <div className={classes.actionsRow}>
                            {hasKlientCard ? (
                                <Button
                                    size="small"
                                    className={classes.btnGhost}
                                    variant="outlined"
                                    onClick={() => setKlientCardOpen(true)}
                                >
                                    Карточка клиента
                                </Button>
                            ) : null}
                            <Button size="small" className={classes.btnPrimary} variant="contained" onClick={handleOpenClientCard}>
                                Оформить заказ
                            </Button>
                        </div>
                    </DialogActions>
                </Dialog>
                {hasKlientCard && (
                    <KlientInfoDialog
                        open2={klientCardOpen}
                        onConfirmDialogClose={() => setKlientCardOpen(false)}
                        idKlient={klientContainer}
                        title="Карточка клиента"
                        onYesClick={() => setKlientCardOpen(false)}
                        yesButton={null}
                        noButton="Закрыть"
                        updateKlientInfoCard={() => {}}
                    />
                )}
            </>
        );
    }, [activeCall, klientInfo, klientCardOpen, callDialogMinimized, referenceStatusOrderAll, canEditOrders, canViewOrders, orderOpenMode]);

    useEffect(() => {
        // Удобный хелпер для ручного теста из консоли
        if (typeof window !== 'undefined') {
            window.telephonyFakeCall = requestFakeCall;
        }
    }, [requestFakeCall]);

    return (
        <TelephonyContext.Provider
            value={{
                calls,
                queueCalls,
                activeCall,
                telephonyMeta,
                triggerFakeCall,
                requestFakeCall,
                openCallDialog,
                minimizeCallDialog,
                closeActiveCall: closeActive,
                markCallAsAccepted,
                markCallAsCallbackDone,
                setOperatorPause,
                openCallById,
            }}
        >
            {children}
            {dialogContent}
        </TelephonyContext.Provider>
    );
};

export const useTelephony = () => useContext(TelephonyContext);

export default TelephonyContext;
